<template>
<div class="hris-page">
  <div class="hris-page-inner">
    <div class="hris-page-header hris-page-header-row">
      <div>
        <span class="hris-page-eyebrow">System Settings</span>
        <h1 class="hris-page-title">Feature Management</h1>
        <p class="hris-page-subtitle">Manage the feature catalog and control which features each tenant can access.</p>
      </div>
      <q-btn flat round dense class="hris-q-ghost" :loading="refreshing" @click.stop="refreshAll">
        <i class="fas fa-sync-alt"></i>
        <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">Refresh Data</q-tooltip>
      </q-btn>
    </div>

    <div class="hris-panel">
      <div class="hris-toolbar" style="margin-bottom: 16px; box-shadow: none; border: none; padding: 0;">
        <h3 class="hris-panel-title" style="margin-bottom: 0;">Features</h3>
        <div class="hris-toolbar-spacer"></div>
        <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="Create Feature" @click="openCreateFeatureModal"></q-btn>
      </div>
      <q-table
        flat
        :data="features"
        :columns="featureColumns"
        row-key="id"
        :pagination.sync="pagination"
        :loading="loadingFeatures"
      >
        <template v-slot:loading>
          <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
            <q-spinner color="green" size="40px"></q-spinner>
            <div>Loading features...</div>
          </div>
        </template>
        <template v-slot:body="props">
          <q-tr :props="props">
            <q-td key="counter" :props="props">{{ props.row.counter }}</q-td>
            <q-td key="name" :props="props">{{ props.row.name }}</q-td>
            <q-td key="slug" :props="props">{{ props.row.slug }}</q-td>
            <q-td key="is_active" :props="props">
              <span class="hris-badge" :class="props.row.is_active === 1 ? 'hris-badge-success' : 'hris-badge-danger'">
                {{ props.row.is_active === 1 ? 'Active' : 'Inactive' }}
              </span>
            </q-td>
            <q-td key="action" :props="props">
              <q-btn dense flat round class="hris-q-ghost" @click.stop="editFeature(props.row)">
                <i class="fas fa-edit"></i>
                <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">Edit Feature</q-tooltip>
              </q-btn>
              <q-btn dense flat round class="hris-q-ghost" @click.stop="confirmDeleteFeature(props.row)">
                <i class="fas fa-trash"></i>
                <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">Delete Feature</q-tooltip>
              </q-btn>
            </q-td>
          </q-tr>
        </template>
        <template v-slot:no-data="">
          <div class="hris-empty full-width">No features found.</div>
        </template>
      </q-table>
    </div>

    <div class="hris-panel">
      <h3 class="hris-panel-title">Company Feature Access</h3>
      <div class="hris-form-row" style="align-items: flex-end;">
        <div class="hris-form-field" style="flex: 1 1 260px;">
          <q-select
            v-model="selectedTenantId"
            :options="tenants"
            option-label="name"
            option-value="id"
            outlined
            dense
            color="green"
            emit-value
            map-options
            label="Select Tenant"
          ></q-select>
        </div>
        <q-btn unelevated class="hris-q-outline" label="Load Features" @click="loadTenantFeatures" :disable="!selectedTenantId"></q-btn>
        <q-btn flat class="hris-q-ghost" label="Reset Toggles" @click="resetCompanyToggles" :disable="!selectedTenantId || loadingCompanyFeatures"></q-btn>
        <q-btn unelevated class="hris-q-primary" label="Save Company Features" @click="saveCompanyFeatures" :disable="!selectedTenantId" :loading="savingCompanyFeatures"></q-btn>
      </div>
      <q-table
        flat
        :data="companyFeatures"
        :columns="companyFeatureColumns"
        row-key="id"
        :loading="loadingCompanyFeatures"
      >
        <template v-slot:loading>
          <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
            <q-spinner color="green" size="40px"></q-spinner>
            <div>Loading company features...</div>
          </div>
        </template>
        <template v-slot:body="props">
          <q-tr :props="props">
            <q-td key="name" :props="props">{{ props.row.name }}</q-td>
            <q-td key="slug" :props="props">{{ props.row.slug }}</q-td>
            <q-td key="is_active" :props="props">
              <span class="hris-badge" :class="props.row.is_active === 1 ? 'hris-badge-success' : 'hris-badge-danger'">
                {{ props.row.is_active === 1 ? 'Active' : 'Inactive' }}
              </span>
            </q-td>
            <q-td key="enabled" :props="props">
              <q-toggle v-model="props.row.enabled" :disable="props.row.is_active !== 1" color="green"></q-toggle>
            </q-td>
          </q-tr>
        </template>
        <template v-slot:no-data="">
          <div class="hris-empty full-width">Select a tenant and load features to manage access.</div>
        </template>
      </q-table>
    </div>

    <q-dialog v-model="featureModal" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">{{ featureForm.tempID ? 'Update Feature' : 'Create Feature' }}</h2>
          <q-btn flat round dense class="hris-q-ghost" :disable="savingFeature" @click.stop="closeFeatureModal">
            <i class="fas fa-times"></i>
          </q-btn>
        </div>
        <div class="hris-modal-body">
          <q-form ref="featureForm" @submit="saveFeature">
            <div class="hris-form-field q-mb-md">
              <q-input v-model="featureForm.name" label="Feature Name" outlined dense color="green" :rules="[formRules.required]"></q-input>
            </div>
            <div class="hris-form-field q-mb-md">
              <q-input v-model="featureForm.slug" label="Slug (e.g. geofencing)" outlined dense color="green" :rules="[formRules.required]"></q-input>
            </div>
            <div class="hris-form-field">
              <q-select
                v-model="featureForm.is_active"
                :options="statusOptions"
                option-label="label"
                option-value="value"
                outlined
                dense
                color="green"
                emit-value
                map-options
                label="Status"
              ></q-select>
            </div>
          </q-form>
        </div>
        <div class="hris-modal-footer">
          <q-btn flat class="hris-q-ghost" label="Cancel" :disable="savingFeature" @click.stop="closeFeatureModal"></q-btn>
          <q-btn unelevated class="hris-q-primary" :loading="savingFeature" label="Save" @click="saveFeature"></q-btn>
        </div>
      </div>
    </q-dialog>

    <q-dialog v-model="deleteModal" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Delete Feature</h2>
        </div>
        <div class="hris-modal-body">
          Are you sure you want to delete <strong>{{ deleteFeatureName }}</strong>?
        </div>
        <div class="hris-modal-footer">
          <q-btn flat label="Cancel" class="hris-q-ghost" @click="closeDeleteModal"></q-btn>
          <q-btn unelevated label="Delete" class="hris-q-primary" :loading="deletingFeature" @click="removeFeature"></q-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';

export default {
  mixins: [mixins],
  data() {
    return {
      loadingFeatures: false,
      loadingCompanyFeatures: false,
      savingFeature: false,
      savingCompanyFeatures: false,
      refreshing: false,
      deletingFeature: false,
      deletingFeatureId: null,
      featureModal: false,
      deleteModal: false,
      deleteFeatureName: '',
      selectedTenantId: null,
      tenants: [],
      features: [],
      companyFeatures: [],
      companyFeaturesSnapshot: [],
      featureForm: {
        tempID: 0,
        name: '',
        slug: '',
        is_active: 1
      },
      statusOptions: [
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 0 }
      ],
      pagination: {
        rowsPerPage: 20
      },
      featureColumns: [
        { name: 'counter', align: 'center', label: '#', field: 'counter', sortable: true },
        { name: 'name', align: 'left', label: 'Feature Name', field: 'name', sortable: true },
        { name: 'slug', align: 'left', label: 'Slug', field: 'slug', sortable: true },
        { name: 'is_active', align: 'center', label: 'Status', field: 'is_active', sortable: true },
        { name: 'action', align: 'center', label: 'Action', field: 'action' }
      ],
      companyFeatureColumns: [
        { name: 'name', align: 'left', label: 'Feature Name', field: 'name', sortable: true },
        { name: 'slug', align: 'left', label: 'Slug', field: 'slug', sortable: true },
        { name: 'is_active', align: 'center', label: 'Catalog Status', field: 'is_active', sortable: true },
        { name: 'enabled', align: 'center', label: 'Enabled For Company', field: 'enabled' }
      ]
    };
  },
  created() {
    this.loadFeatures();
    this.loadTenants();
  },
  watch: {
    selectedTenantId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.onTenantChange();
      }
    }
  },
  methods: {
    closeFeatureModal() {
      this.featureModal = false;
      this.resetFeatureForm();
    },
    closeDeleteModal() {
      this.deleteModal = false;
      this.deleteFeatureName = '';
      this.deletingFeatureId = null;
      this.deletingFeature = false;
    },
    openCreateFeatureModal() {
      this.resetFeatureForm();
      this.featureModal = true;
    },
    refreshAll() {
      this.refreshing = true;
      Promise.all([
        this.loadFeatures(),
        this.loadTenants(),
        this.selectedTenantId ? this.loadTenantFeatures() : Promise.resolve()
      ]).finally(() => {
        this.refreshing = false;
      });
    },
    loadFeatures() {
      this.loadingFeatures = true;
      return axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).get('/admin/features')
        .then((res) => {
          this.features = res.data.data || [];
        })
        .finally(() => {
          this.loadingFeatures = false;
        });
    },
    loadTenants() {
      return axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).get('/admin/tenants')
        .then((res) => {
          this.tenants = res.data.data || [];
          if (!this.selectedTenantId && this.tenants.length > 0) {
            this.selectedTenantId = this.tenants[0].id;
          }
        });
    },
    onTenantChange() {
      this.companyFeatures = [];
      this.companyFeaturesSnapshot = [];
      this.loadTenantFeatures();
    },
    loadTenantFeatures() {
      if (!this.selectedTenantId) return;

      this.loadingCompanyFeatures = true;
      return axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).get(`/admin/company/features/${this.selectedTenantId}`)
        .then((res) => {
          this.companyFeatures = (res.data.data || []).map(feature => ({ ...feature }));
          this.companyFeaturesSnapshot = this.companyFeatures.map(feature => ({ ...feature }));
        })
        .finally(() => {
          this.loadingCompanyFeatures = false;
        });
    },
    resetCompanyToggles() {
      this.companyFeatures = this.companyFeaturesSnapshot.map(feature => ({ ...feature }));
    },
    saveFeature() {
      const isValid = this.$refs.featureForm && this.$refs.featureForm.validate
        ? this.$refs.featureForm.validate()
        : true;

      if (!isValid) {
        return;
      }

      if (!this.featureForm.name || !this.featureForm.slug) {
        this.$q.notify({ color: 'red', message: 'Name and slug are required.' });
        return;
      }

      this.savingFeature = true;
      const formData = new FormData();
      formData.append('tempID', this.featureForm.tempID);
      formData.append('name', this.featureForm.name);
      formData.append('slug', this.featureForm.slug);
      formData.append('is_active', this.featureForm.is_active);

      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post('/admin/features', formData)
        .then(() => {
          this.$q.notify({ color: 'green', message: 'Feature saved successfully.' });
          this.closeFeatureModal();
          this.loadFeatures();
          this.loadTenantFeatures();
        })
        .catch((error) => {
          this.$q.notify({
            color: 'red',
            message: (error.response && error.response.data && error.response.data.message) || 'Failed to save feature.'
          });
        })
        .finally(() => {
          this.savingFeature = false;
        });
    },
    editFeature(feature) {
      this.featureForm = {
        tempID: feature.id,
        name: feature.name,
        slug: feature.slug,
        is_active: feature.is_active
      };
      this.featureModal = true;
    },
    confirmDeleteFeature(feature) {
      this.deletingFeatureId = feature.id;
      this.deleteFeatureName = feature.name;
      this.deleteModal = true;
    },
    removeFeature() {
      if (!this.deletingFeatureId) return;
      this.deletingFeature = true;
      const formData = new FormData();
      formData.append('tempID', this.deletingFeatureId);

      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post('/admin/features/delete', formData)
        .then(() => {
          this.$q.notify({ color: 'green', message: 'Feature removed.' });
          this.closeDeleteModal();
          this.loadFeatures();
          this.loadTenantFeatures();
        })
        .catch((error) => {
          this.$q.notify({
            color: 'red',
            message: (error.response && error.response.data && error.response.data.message) || 'Failed to remove feature.'
          });
        })
        .finally(() => {
          this.deletingFeature = false;
          this.deletingFeatureId = null;
        });
    },
    saveCompanyFeatures() {
      if (!this.selectedTenantId) return;

      this.savingCompanyFeatures = true;
      const featureIds = this.companyFeatures
        .filter(feature => feature.enabled === true && feature.is_active === 1)
        .map(feature => feature.id);

      const formData = new FormData();
      formData.append('company_id', this.selectedTenantId);
      formData.append('feature_ids', JSON.stringify(featureIds));

      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post('/admin/company/features', formData)
        .then(() => {
          this.$q.notify({ color: 'green', message: 'Company feature subscription updated.' });
        })
        .finally(() => {
          this.savingCompanyFeatures = false;
        });
    },
    resetFeatureForm() {
      this.featureForm = {
        tempID: 0,
        name: '',
        slug: '',
        is_active: 1
      };
    }
  }
};
</script>
